/**
 * Shared Crew Mate catalog — single source of truth (C0 / Run 6).
 *
 * Consumers:
 * - web/lib/crew/mates.ts (re-exports + feed/LosiAvatar bridge)
 * - crew-mate/src/mates/catalog.ts (re-exports + mateSrc)
 * - mobile/lib/crew/mates.ts (Metro cannot import packages/ — lockstep mirror)
 *
 * Parity lock: web/__tests__/lib/crew-mates-catalog-parity.test.ts
 * Plush JPGs live at crew-mate/public/mates/{id}.jpg
 *
 * Naming: petal display label is **Peta** (id stays `petal`). No Muse/Vesper.
 * Preserve LosiAnimated on product surfaces (engine shapes stay separate).
 */

export type MateId =
  | 'petal'
  | 'jovi'
  | 'star'
  | 'alpaca'
  | 'axolotl'
  | 'bat'
  | 'bear'
  | 'beaver'
  | 'bunny'
  | 'capybara'
  | 'cat'
  | 'deer'
  | 'dino'
  | 'dog'
  | 'dragon'
  | 'duck'
  | 'elephant'
  | 'flamingo'
  | 'fox'
  | 'frog'
  | 'giraffe'
  | 'hamster'
  | 'hedgehog'
  | 'hippo'
  | 'koala'
  | 'lion'
  | 'monkey'
  | 'mouse'
  | 'otter'
  | 'owl'
  | 'panda'
  | 'parrot'
  | 'penguin'
  | 'raccoon'
  | 'redpanda'
  | 'shark'
  | 'sheep'
  | 'sloth'
  | 'squirrel'
  | 'tiger'
  | 'turtle'
  | 'unicorn'
  | 'wolf'
  | 'zebra'

export type Mate = {
  id: MateId
  label: string
  /** Soft stage wash behind the plush. */
  accent: string
}

/** Bloub silhouettes (shape mates). petal display = Peta. */
export const CREW_SHAPES: Mate[] = [
  { id: 'petal', label: 'Peta', accent: '#E8DCC8' },
  { id: 'jovi', label: 'Jovi', accent: '#E8DCC8' },
  { id: 'star', label: 'Star', accent: '#E8DCC8' }
]

/** Animal plush roster — ids must match crew-mate/public/mates/*.jpg */
export const CREW_ANIMAL_MATES: Mate[] = [
  { id: 'fox', label: 'Fox', accent: '#E07A3A' },
  { id: 'cat', label: 'Cat', accent: '#8B8F98' },
  { id: 'bunny', label: 'Bunny', accent: '#E8D5C4' },
  { id: 'owl', label: 'Owl', accent: '#8B6914' },
  { id: 'bear', label: 'Bear', accent: '#8B5A2B' },
  { id: 'dog', label: 'Dog', accent: '#C4A574' },
  { id: 'frog', label: 'Frog', accent: '#6B9E4A' },
  { id: 'raccoon', label: 'Raccoon', accent: '#6B6B6B' },
  { id: 'panda', label: 'Panda', accent: '#2A2A2A' },
  { id: 'penguin', label: 'Penguin', accent: '#3D5A80' },
  { id: 'otter', label: 'Otter', accent: '#A67C52' },
  { id: 'hedgehog', label: 'Hedgehog', accent: '#7A5C3E' },
  { id: 'koala', label: 'Koala', accent: '#9AA0A6' },
  { id: 'sloth', label: 'Sloth', accent: '#A89070' },
  { id: 'capybara', label: 'Capybara', accent: '#B8956A' },
  { id: 'hamster', label: 'Hamster', accent: '#D4A574' },
  { id: 'mouse', label: 'Mouse', accent: '#C8B8A8' },
  { id: 'squirrel', label: 'Squirrel', accent: '#C4783A' },
  { id: 'beaver', label: 'Beaver', accent: '#8B5A2B' },
  { id: 'sheep', label: 'Sheep', accent: '#E8E4DC' },
  { id: 'alpaca', label: 'Alpaca', accent: '#D4C4A8' },
  { id: 'deer', label: 'Deer', accent: '#A67C52' },
  { id: 'wolf', label: 'Wolf', accent: '#6B7A8A' },
  { id: 'lion', label: 'Lion', accent: '#D4A017' },
  { id: 'tiger', label: 'Tiger', accent: '#E07A2A' },
  { id: 'redpanda', label: 'Red panda', accent: '#C45C26' },
  { id: 'elephant', label: 'Elephant', accent: '#8A8F98' },
  { id: 'hippo', label: 'Hippo', accent: '#8B7A8A' },
  { id: 'giraffe', label: 'Giraffe', accent: '#D4A84B' },
  { id: 'zebra', label: 'Zebra', accent: '#3A3A3A' },
  { id: 'monkey', label: 'Monkey', accent: '#A67C52' },
  { id: 'duck', label: 'Duck', accent: '#F0C040' },
  { id: 'flamingo', label: 'Flamingo', accent: '#F4A6B8' },
  { id: 'parrot', label: 'Parrot', accent: '#3DB88A' },
  { id: 'bat', label: 'Bat', accent: '#5A4A6A' },
  { id: 'axolotl', label: 'Axolotl', accent: '#F4A0B8' },
  { id: 'turtle', label: 'Turtle', accent: '#5A8F5A' },
  { id: 'shark', label: 'Shark', accent: '#5A7A8A' },
  { id: 'dino', label: 'Dino', accent: '#6B9E5A' },
  { id: 'dragon', label: 'Dragon', accent: '#6B4A8A' },
  { id: 'unicorn', label: 'Unicorn', accent: '#C8A0E8' }
]

export const ALL_CREW_MATES: Mate[] = [...CREW_SHAPES, ...CREW_ANIMAL_MATES]

export const DEFAULT_MATE_ID: MateId = 'petal'

export const MATE_BY_ID = new Map(
  ALL_CREW_MATES.map(mate => [mate.id, mate] as const)
)

export function isMateId(value: string): value is MateId {
  return MATE_BY_ID.has(value as MateId)
}

/**
 * Resolve raw id → catalog MateId.
 * Accepts engine shape aliases etoile→star, peta→petal.
 */
export function resolveMateId(raw: string | null | undefined): MateId | null {
  if (!raw) return null
  const normalized = raw.trim().toLowerCase()
  if (normalized === 'etoile') return 'star'
  if (normalized === 'peta') return 'petal'
  return isMateId(normalized) ? normalized : null
}

/** crew-mate / CDN plush path convention. */
export function mateSrc(id: MateId): string {
  return `/mates/${id}.jpg`
}

/** Aliases kept for crew-mate historical names. */
export const SHAPES = CREW_SHAPES
export const MATES = CREW_ANIMAL_MATES
export const ALL_MATES = ALL_CREW_MATES
export const DEFAULT_MATE = DEFAULT_MATE_ID
